import http from 'common/http'
import type { AxiosResponse } from 'axios'

export type TrxLogPage = {
  results: Array<{
    instanceId: number
    serialNumber: string
    recordNumber: number | string
  }>
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

export type TrxStreamQueryFilters = {
  type?: string
  serialNumber?: string
  instanceId?: string
  recordNumber?: string
  uploadFrom?: string
  uploadTo?: string
  startFrom?: string
  startTo?: string
  harvestId?: number
}

export const fetchTrxStats = () => {
  return http.get('/trx/stats', {})
}

export const fetchTrxStreamsPaginated = (
  page: number = 1,
  pageSize: number = 20,
  filters: TrxStreamQueryFilters = {},
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  })
  if (filters.type) params.set('type', filters.type)
  if (filters.serialNumber) params.set('serialNumber', filters.serialNumber)
  if (filters.instanceId) params.set('instanceId', filters.instanceId)
  if (filters.recordNumber) params.set('recordNumber', filters.recordNumber)
  if (filters.uploadFrom) params.set('uploadFrom', filters.uploadFrom)
  if (filters.uploadTo) params.set('uploadTo', filters.uploadTo)
  if (filters.type === 'trx' && filters.startFrom) {
    params.set('startFrom', filters.startFrom)
  }
  if (filters.type === 'trx' && filters.startTo) {
    params.set('startTo', filters.startTo)
  }
  if (filters.harvestId != null) {
    params.set('harvestId', filters.harvestId.toString())
  }
  return http.get(`/trx/stream?${params.toString()}`, {})
}

export const downloadTrxStream = (
  instanceId: number,
  serialNumber: string,
  // recordNumber is a PostgreSQL BIGINT and arrives as a string from the
  // listing endpoint (see TrxStreamData.recordNumber) - accept either so
  // callers can pass a listing row's value straight through unchanged.
  recordNumber: number | string,
  type: string,
) => {
  const params = new URLSearchParams({
    instanceId: instanceId.toString(),
    serialNumber,
    recordNumber: recordNumber.toString(),
    type,
  })
  return http.get(`/trx/stream/download?${params.toString()}`, {
    responseType: 'blob',
  })
}

export type TrxStreamZipFile = {
  instanceId: number
  serialNumber: string
  recordNumber: number | string
  fileType: string
}

// Keep in sync with MAX_ZIP_DOWNLOAD_FILES in packages/rest-api/routes/trx.ts
export const MAX_ZIP_DOWNLOAD_FILES = 1000

export const downloadTrxStreamsZip = (files: TrxStreamZipFile[]) => {
  return http.post(
    '/trx/stream/download/zip',
    { files },
    { responseType: 'arraybuffer' },
  )
}

export const getTrxLogsPaged = (
  page,
  pageSize,
  hardwareId,
  options: { instanceId?: number; recordNumber?: number },
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
    hardwareId,
  })

  if (options.instanceId !== undefined) {
    params.set('instanceId', options.instanceId.toString())
  }
  if (options.recordNumber !== undefined) {
    params.set('recordNumber', options.recordNumber.toString())
  }

  return http.get('/trx/log', { params }) as Promise<AxiosResponse<TrxLogPage>>
}
